import React, { useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { COMTRUEVAR } from 'assets/resources/comtrue/js/default/ocr_variables'
import { useLazySelectAth0201Query } from 'app/core/services/com/ocr.service'
import { showAlert } from 'app/shared/utils/util'
import { comUtil } from 'app/shared/utils'
import { closeThis } from 'assets/resources/comtrue/js/default/common'
import { useLazySelectCom0102Query } from 'app/core/services/com/com.service'

/*======================================================================
 * @ 업무설명 : 안면인증 컴트루 솔루션 호출
 * @ 파 일 명 : src\app\pages\com\ocr\ComtrueStart.jsx
 * @ 화면 ID  :
 * @ASIS :
 *======================================================================
 * 번호  작업자           작업일         변경내용
 *======================================================================
 * 1     최정기(S24068)   2025.06.19    최초작성
 *======================================================================
 */

const ComtrueStart = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const [fetchAth0201] = useLazySelectAth0201Query()
    const [selectCom0102] = useLazySelectCom0102Query()
    const resultAth0201 = useRef(null)
    const resultFetch0102 = useRef(null)
    const errMsg = useRef(null)
    const errMsgCheck = useRef(false)
    const blackErrorCodeList = [
        'BXCME00053',
        'BXCME00054',
        'BXCME00055',
        'BXCME00056',
        'BXCME00057',
        'BXCME00058',
        'BXCME00059',
        'BXCME00060',
        'BXCME00061',
        'BXCME00062',
        'BXCME00063',
        'BXCME00064',
        'BXCME00065',
        'BXCME00066',
        'BXCME00067',
        'BXCME00068',
        'BXCME00069',
        'BXCME00070',
    ]

    // 세션 스토리지에 파라미터 저장
    const saveParamsToSession = (params) => {
        for (const [key, value] of params.entries()) {
            sessionStorage.setItem(key, value)
        }
    }

    // 세션 스토리지에서 파라미터 가져오기
    const getParamsFromSession = () => {
        return {
            cardType: sessionStorage.getItem('cardType'),
            callback: sessionStorage.getItem('callback'),
            liveness: sessionStorage.getItem('liveness'),
            licenseKey: sessionStorage.getItem('licenseKey'),
            contextPath: sessionStorage.getItem('contextPath'),
            contextPathComtrue: sessionStorage.getItem('contextPathComtrue'),
            debugModeComtrue: sessionStorage.getItem('debugModeComtrue'),
        }
    }

    useEffect(() => {
        comUtil.setUserData('globalLoading', false)
    }, [])

    useEffect(() => {
        // URL에 파라미터가 있으면 세션에 저장 : cardType 기준으로 확인
        if (searchParams.get('cardType')) {
            saveParamsToSession(searchParams)
        }

        // 파라미터 가져오기 (URL 또는 세션스토리지에서) cardType 기준으로 확인
        const params = searchParams.get('cardType')
            ? Object.fromEntries(searchParams.entries())
            : getParamsFromSession()

        // URL 파라미터에서 데이터 가져오기
        // 새로고침시에도 변수 유지를 위해 startOcr 밖에서 확인
        const cardType = params.cardType
        const callback = params.callback
        const liveness = params.liveness
        const licenseKey = params.licenseKey
        const contextPath = params.contextPath
        const contextPathComtrue = params.contextPathComtrue
        const debugModeComtrue = params.debugModeComtrue

        // [2025.06.10] OcrPopupTest.jsx 에서 이관 및 일부 코드 수정
        const startOcr = async () => {
            COMTRUEVAR.cardTypeVarComtrue = cardType
            COMTRUEVAR.ocrLiveness = liveness
            COMTRUEVAR.contextPathComtrue = contextPathComtrue
            COMTRUEVAR.debugModeComtrue = debugModeComtrue

            try {
                // 1. proxy/start ======================================================================

                resultFetch0102.current = await selectCom0102({
                    dsrnTgtBacd: '110',
                    ccddId: '',
                    cdgpId: 'OCR_ATRB_VL',
                })

                if (resultFetch0102.current.isError) {
                    errMsg.current =
                        resultFetch0102.current.error['data']?.bscMsg
                    errMsgCheck.current = true
                    return
                }

                resultFetch0102.current?.data.data.map((x) => {
                    if (x.ccddId === 'OCR_MAX_CNT') {
                        COMTRUEVAR.OCR_MAX_CNT = Number(x.cdAtrbEtc1Con)
                    } else if (x.ccddId === 'OCR_INIT_DELAY') {
                        COMTRUEVAR.OCR_INIT_DELAY = Number(x.cdAtrbEtc1Con)
                    } else if (x.ccddId === 'OCR_INTERVAL') {
                        COMTRUEVAR.OCR_INTERVAL = Number(x.cdAtrbEtc1Con)
                    } else if (x.ccddId === 'OCR_LKEY') {
                        COMTRUEVAR.licenseKey = x.cdAtrbEtc1Con
                    }
                })

                resultAth0201.current = await fetchAth0201({
                    liveness: 'true',
                    encryptView: 'true',
                })

                if (resultAth0201.current.isError) {
                    errMsg.current = resultAth0201.current.error['data']?.bscMsg
                    errMsgCheck.current = true
                    return
                }
                // 필요한 정보 저장
                // [2025.06.10] 암호화 사용시에만 저장하도록 조건처리
                COMTRUEVAR.ocrLiveness = true
                COMTRUEVAR.initTime = Number(
                    resultAth0201.current?.data.initTime
                )
                COMTRUEVAR.encryptView = 'true'
                COMTRUEVAR.rsaKeys = JSON.parse(
                    resultAth0201.current?.data.rsaKeys
                )
                COMTRUEVAR.tokenComtrue = resultAth0201.current?.data.token

                if (COMTRUEVAR.encryptView) {
                    localStorage.setItem(
                        'pubKey',
                        COMTRUEVAR.rsaKeys.requestKeys.pubKey
                    )
                    localStorage.setItem(
                        'priKey',
                        COMTRUEVAR.rsaKeys.requestKeys.priKey
                    )
                }

                // hasStarted 설정 후 리디렉션
                sessionStorage.setItem('hasStarted', 'true')
            } catch (err) {
                console.error('OCR 오류:', err)
                showAlert({
                    msg: 'OCR 시작 중 오류 발생: ' + err,
                })
            }
        }

        ;(async () => {
            try {
                // startOcr 호출
                await startOcr()

                if (errMsgCheck.current) {
                    showAlert({
                        msg: errMsg.current,
                        onConfirm: () => {
                            closeThis()
                        },
                    })
                    return
                } else {
                    // 리디렉션
                    const redirectPage =
                        COMTRUEVAR.cardTypeVarComtrue == 'general'
                            ? '/com/ocr/idcard'
                            : COMTRUEVAR.cardTypeVarComtrue == 'kpassport'
                            ? '/com/ocr/kpassport'
                            : '/com/ocr/passport'
                    navigate(redirectPage)
                }
            } catch (error) {
                console.error('요청실패: ', error)
                showAlert({
                    msg: '요청 중 오류 발생',
                })
            }
        })()
        return () => {
            // ocr 종료 시 설정
            sessionStorage.setItem('hasStarted', 'false')
        }
    }, [navigate])

    return (
        <div className="theme-box" id="ibkScreen">
            <div className="ibk-loading">
                <div className="ibk-loading-box">
                    <img
                        src="/assets/images/common/icon-loading-ani.svg"
                        alt="로딩중"
                        width={92}
                        height={90}
                    />
                    <h2 className="h4 mt-32">카메라를 불러오는 중입니다.</h2>
                    <p className="mt-04">잠시만 기다려주세요.</p>
                </div>
            </div>
        </div>
    )
}

export default ComtrueStart
